import { useEffect, useCallback } from 'react';
import { useAuth } from 'react-oidc-context';
import { useDispatch, useSelector } from 'react-redux';

import { APP_NAME } from '@/constants';
import { refreshTokenConfig } from '@/oidcConfig';
import { fetchAuditableElements } from '@/slices/auditableElementsSlice';
import { refreshSignIn, clearOnLogOut } from '@/slices/authSlice';
import { RootState } from '@/store';

export const useTokenAutoRefreshOIDC = () => {
  const dispatch = useDispatch();
  const { events, signinSilent, signoutSilent } = useAuth();

  const authSlice = useSelector((state: RootState) => state.auth);
  const authSliceMessage = authSlice?.status?.message;
  const { isOidcClient } = authSlice;

  const handleTokenExpiring = useCallback(async () => {
    try {
      const user = await signinSilent();
      const { needRefresh } = refreshTokenConfig(authSlice);

      if (user && needRefresh && authSliceMessage !== 'loading') {
        dispatch(
          refreshSignIn({
            accessToken: user.access_token,
            idToken: user.id_token,
            refreshToken: user.refresh_token,
            expiresIn: user.expires_in
          })
        );

        dispatch(
          fetchAuditableElements({
            applicationName: APP_NAME
          }) as any
        );
      }
    } catch (err) {
      console.error('❌ Error in handleTokenExpiring', err);
      signoutSilent({
        id_token_hint: authSlice?.idToken
      });

      dispatch(clearOnLogOut() as any);
      localStorage.clear();
    }
  }, [dispatch, signinSilent, authSlice, authSliceMessage]);

  useEffect(() => {
    if (!isOidcClient || !events) return;
    events.addAccessTokenExpiring(handleTokenExpiring);

    return () => {
      events.removeAccessTokenExpiring(handleTokenExpiring);
    };
  }, [isOidcClient, events, handleTokenExpiring]);
};
